(() => {
  'use strict';

  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const money = (amount, currency = 'EUR', fromSubunits = true) => {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) return '';
    const value = fromSubunits ? numericAmount / 100 : numericAmount;
    return new Intl.NumberFormat(document.documentElement.lang || 'it-IT', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const toast = (message) => {
    let element = $('[data-pnw-toast]');
    if (!element) {
      element = document.createElement('div');
      element.className = 'pnw-toast';
      element.setAttribute('data-pnw-toast', '');
      element.setAttribute('role', 'status');
      element.setAttribute('aria-live', 'polite');
      document.body.appendChild(element);
    }
    element.textContent = message;
    element.classList.add('is-visible');
    window.clearTimeout(element._pnwTimer);
    element._pnwTimer = window.setTimeout(() => element.classList.remove('is-visible'), 3200);
  };

  const updateCartCount = async () => {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      $$('[data-pnw-cart-count]').forEach((badge) => {
        badge.textContent = cart.item_count;
        badge.hidden = cart.item_count === 0;
      });
    } catch (_) {
      // The cart remains usable through the normal Shopify flow.
    }
  };

  const initQuickAdd = () => {
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('[data-pnw-quick-add]');
      if (!form) return;
      event.preventDefault();
      const button = $('button[type="submit"]', form);
      const original = button ? button.innerHTML : '';
      if (button) {
        button.disabled = true;
        button.textContent = 'Aggiungo…';
      }
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        if (!response.ok) throw new Error('add_failed');
        await updateCartCount();
        toast('Prodotto aggiunto al carrello.');
      } catch (_) {
        toast('Non è stato possibile aggiungere il prodotto. Apri la scheda e riprova.');
      } finally {
        if (button) {
          button.disabled = false;
          button.innerHTML = original;
        }
      }
    });
  };

  const initHeader = () => {
    const categories = $('[data-pnw-categories]');
    const categoriesButton = $('[data-pnw-categories-button]');
    if (categories && categoriesButton) {
      const closeCategories = () => {
        categories.classList.remove('is-open');
        categoriesButton.setAttribute('aria-expanded', 'false');
      };
      categoriesButton.addEventListener('click', (event) => {
        event.stopPropagation();
        const open = categories.classList.toggle('is-open');
        categoriesButton.setAttribute('aria-expanded', String(open));
      });
      document.addEventListener('click', (event) => {
        if (!categories.contains(event.target)) closeCategories();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeCategories();
      });
    }

    const drawer = $('[data-pnw-mobile-drawer]');
    const drawerOpen = $('[data-pnw-mobile-open]');
    const drawerClose = $('[data-pnw-mobile-close]');
    if (drawer && drawerOpen) {
      const setDrawer = (open) => {
        drawer.classList.toggle('is-open', open);
        drawer.setAttribute('aria-hidden', String(!open));
        drawerOpen.setAttribute('aria-expanded', String(open));
        document.body.classList.toggle('pnw-menu-open', open);
        if (open && drawerClose) drawerClose.focus();
      };
      drawerOpen.addEventListener('click', () => setDrawer(true));
      drawerClose?.addEventListener('click', () => setDrawer(false));
      drawer.addEventListener('click', (event) => {
        if (event.target === drawer) setDrawer(false);
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') setDrawer(false);
      });
    }
  };

  const initPredictiveSearch = () => {
    $$('[data-pnw-search]').forEach((form) => {
      const input = $('input[type="search"]', form);
      const panel = $('[data-pnw-suggestions]', form.parentElement || document);
      if (!input || !panel) return;
      let controller;
      let timer;

      const close = () => {
        panel.classList.remove('is-open');
        panel.innerHTML = '';
      };

      const build = (products, term) => {
        panel.innerHTML = '';
        const head = document.createElement('div');
        head.className = 'pnw-suggest-head';
        head.textContent = products.length ? 'Prodotti suggeriti' : 'Nessun risultato immediato';
        panel.appendChild(head);

        products.slice(0, 6).forEach((product) => {
          const link = document.createElement('a');
          link.className = 'pnw-suggest-item';
          link.href = product.url;

          if (product.featured_image?.url) {
            const image = document.createElement('img');
            image.src = product.featured_image.url;
            image.alt = product.featured_image.alt || product.title || '';
            image.loading = 'lazy';
            link.appendChild(image);
          } else {
            const placeholder = document.createElement('span');
            placeholder.style.width = '54px';
            link.appendChild(placeholder);
          }

          const copy = document.createElement('span');
          const title = document.createElement('span');
          title.className = 'pnw-suggest-item__title';
          title.textContent = product.title || '';
          copy.appendChild(title);
          if (product.vendor) {
            const vendor = document.createElement('span');
            vendor.className = 'pnw-suggest-item__vendor';
            vendor.textContent = product.vendor;
            copy.appendChild(vendor);
          }
          link.appendChild(copy);

          const price = document.createElement('span');
          price.className = 'pnw-suggest-item__price';
          if (product.price) {
            const amount = typeof product.price === 'object' ? product.price.amount : product.price;
            const currency = typeof product.price === 'object' ? (product.price.currency || product.price.currencyCode || 'EUR') : 'EUR';
            price.textContent = money(amount, currency, false);
          }
          link.appendChild(price);
          panel.appendChild(link);
        });

        const all = document.createElement('a');
        all.className = 'pnw-suggest-all';
        all.href = `/search?q=${encodeURIComponent(term)}&type=product`;
        all.textContent = products.length ? `Vedi tutti i risultati per “${term}”` : `Cerca comunque “${term}”`;
        panel.appendChild(all);
        panel.classList.add('is-open');
      };

      const search = async () => {
        const term = input.value.trim();
        if (term.length < 2) {
          close();
          return;
        }
        controller?.abort();
        controller = new AbortController();
        try {
          const url = `/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=show`;
          const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('predictive_search_failed');
          const data = await response.json();
          build(data?.resources?.results?.products || [], term);
        } catch (error) {
          if (error.name !== 'AbortError') close();
        }
      };

      input.addEventListener('input', () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(search, 180);
      });
      input.addEventListener('focus', () => {
        if (panel.children.length) panel.classList.add('is-open');
      });
      document.addEventListener('click', (event) => {
        if (!form.parentElement?.contains(event.target)) close();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
      });
    });
  };

  const initHeroes = () => {
    $$('[data-pnw-hero]').forEach((hero) => {
      const slides = $$('[data-pnw-hero-slide]', hero);
      const dots = $$('[data-pnw-hero-dot]', hero);
      if (slides.length < 2) return;
      let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let timer;

      const show = (next) => {
        index = (next + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
        });
        dots.forEach((dot, dotIndex) => {
          const active = dotIndex === index;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', active ? 'true' : 'false');
        });
      };

      const start = () => {
        if (reduceMotion) return;
        window.clearInterval(timer);
        timer = window.setInterval(() => show(index + 1), Number(hero.dataset.autoplay || 6500));
      };

      dots.forEach((dot, dotIndex) => dot.addEventListener('click', () => {
        show(dotIndex);
        start();
      }));
      hero.addEventListener('mouseenter', () => window.clearInterval(timer));
      hero.addEventListener('mouseleave', start);
      hero.addEventListener('focusin', () => window.clearInterval(timer));
      hero.addEventListener('focusout', start);
      show(index);
      start();
    });
  };

  const initRails = () => {
    $$('[data-pnw-rail-wrap]').forEach((wrap) => {
      const rail = $('[data-pnw-rail]', wrap);
      if (!rail) return;
      $$('[data-pnw-rail-direction]', wrap).forEach((button) => {
        button.addEventListener('click', () => {
          const direction = button.dataset.pnwRailDirection === 'prev' ? -1 : 1;
          rail.scrollBy({ left: direction * Math.max(260, rail.clientWidth * 0.82), behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      });
    });
  };

  const initReveal = () => {
    const items = $$('.pnw-reveal');
    if (!items.length || reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -30px' });
    items.forEach((item) => observer.observe(item));
  };

  const initGallery = () => {
    $$('[data-pnw-gallery]').forEach((gallery) => {
      const main = $('[data-pnw-gallery-main]', gallery);
      const mainImage = $('img', main || gallery);
      if (!mainImage) return;
      $$('[data-pnw-gallery-thumb]', gallery).forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const source = thumb.dataset.imageSrc;
          const alt = thumb.dataset.imageAlt || '';
          if (!source) return;
          $$('[data-pnw-gallery-thumb]', gallery).forEach((item) => item.classList.remove('is-active'));
          thumb.classList.add('is-active');
          mainImage.style.opacity = '0';
          window.setTimeout(() => {
            mainImage.src = source;
            mainImage.srcset = '';
            mainImage.alt = alt;
            mainImage.style.opacity = '1';
          }, 130);
        });
      });
    });
  };

  const initProductForms = () => {
    $$('[data-pnw-product-form]').forEach((form) => {
      const select = $('[data-pnw-variant-select]', form);
      const button = $('[data-pnw-product-submit]', form);
      const scope = form.closest('[data-pnw-product-scope]') || document;
      if (!select) return;
      const update = () => {
        const option = select.options[select.selectedIndex];
        const available = option?.dataset.available === 'true';
        const price = Number(option?.dataset.price || 0);
        const compare = Number(option?.dataset.compare || 0);
        const currency = option?.dataset.currency || 'EUR';
        const priceElement = $('[data-pnw-product-price]', scope);
        const compareElement = $('[data-pnw-product-compare]', scope);
        const skuElement = $('[data-pnw-product-sku]', scope);
        const stockElement = $('[data-pnw-product-stock]', scope);
        if (priceElement) priceElement.textContent = money(price, currency);
        if (compareElement) compareElement.textContent = compare > price ? money(compare, currency) : '';
        if (skuElement) skuElement.textContent = option?.dataset.sku || '—';
        if (stockElement) stockElement.textContent = available ? 'Disponibile per l’ordine' : 'Non disponibile';
        if (button) {
          button.disabled = !available;
          button.textContent = available ? 'Aggiungi al carrello' : 'Non disponibile';
        }
      };
      select.addEventListener('change', update);
      update();
    });
  };

  const initCollection = () => {
    $$('[data-pnw-filter-toggle]').forEach((button) => {
      const target = document.getElementById(button.getAttribute('aria-controls'));
      if (!target) return;
      const close = () => {
        target.classList.remove('is-open');
        button.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('pnw-menu-open');
      };
      button.addEventListener('click', () => {
        const open = target.classList.toggle('is-open');
        button.setAttribute('aria-expanded', String(open));
        document.body.classList.toggle('pnw-menu-open', open);
      });
      $('[data-pnw-filter-close]', target)?.addEventListener('click', close);
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
      });
    });

    $$('[data-pnw-sort]').forEach((select) => {
      select.addEventListener('change', () => select.form?.submit());
    });
  };

  const init = () => {
    initHeader();
    initPredictiveSearch();
    initHeroes();
    initRails();
    initReveal();
    initQuickAdd();
    initGallery();
    initProductForms();
    initCollection();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();

(() => {
  'use strict';

  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let assistanceCoverageAttempts = 0;

  const initAssistanceCoverage = () => {
    const SERVICE_PATH = '/products/assistenza-con-ritiro-computer-entro-15-km';
    const SERVICE_VARIANT_ID = '59333456658777';
    const MAX_DISTANCE_KM = 15;
    const ORIGIN_LAT = 38.1729126;
    const ORIGIN_LON = 13.2387178;
    const currentPath = window.location.pathname.replace(/\/$/, '');

    if (currentPath !== SERVICE_PATH) return;
    if (document.getElementById('PnwAssistanceCoverage')) return;

    const forms = $$('form[action*="/cart/add"]').filter((form) => {
      if (form.matches('[data-pnw-quick-add]') || form.closest('[data-pnw-quick-add]')) return false;
      const idField = $('[name="id"]', form);
      if (!idField) return Boolean(form.closest('[data-pnw-product-scope], .product, product-info, main'));
      return String(idField.value) === SERVICE_VARIANT_ID;
    });

    if (!forms.length) {
      if (assistanceCoverageAttempts < 20) {
        assistanceCoverageAttempts += 1;
        window.setTimeout(initAssistanceCoverage, 250);
      }
      return;
    }

    assistanceCoverageAttempts = 0;
    const mainForm = forms.find((form) => $('[data-pnw-product-submit], [name="add"], button[type="submit"]', form)) || forms[0];
    const state = { verified: false };

    const style = document.createElement('style');
    style.id = 'PnwAssistanceCoverageStyles';
    style.textContent = `
      .pnw-assistance-coverage{margin:18px 0;padding:18px;border:1px solid rgba(13,53,92,.16);border-radius:16px;background:linear-gradient(180deg,#f7fbff 0%,#fff 100%);box-shadow:0 10px 30px rgba(13,53,92,.08)}
      .pnw-assistance-coverage__head{display:flex;gap:12px;align-items:flex-start;margin-bottom:14px}.pnw-assistance-coverage__icon{width:40px;height:40px;display:grid;place-items:center;flex:0 0 40px;border-radius:12px;background:#0d355c;color:#fff}.pnw-assistance-coverage__icon svg{width:22px;height:22px}.pnw-assistance-coverage__head strong{display:block;font-size:16px;line-height:1.25;color:#0d355c}.pnw-assistance-coverage__head small{display:block;margin-top:4px;line-height:1.45;color:#536272}
      .pnw-assistance-coverage label{display:block;margin-bottom:7px;font-size:13px;font-weight:800;color:#172b3f}.pnw-assistance-coverage__row{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:9px}.pnw-assistance-coverage input{min-width:0;width:100%;min-height:48px;padding:0 14px;border:1px solid #cbd6e2;border-radius:11px;background:#fff;color:#172b3f;font:inherit;outline:none}.pnw-assistance-coverage input:focus{border-color:#0d66c2;box-shadow:0 0 0 3px rgba(13,102,194,.12)}
      .pnw-assistance-coverage__verify{min-height:48px;padding:0 17px;border:0;border-radius:11px;background:#0d66c2;color:#fff;font:inherit;font-weight:850;cursor:pointer}.pnw-assistance-coverage__verify:hover{filter:brightness(.96)}.pnw-assistance-coverage__verify:disabled{opacity:.65;cursor:wait}
      .pnw-assistance-coverage__status{min-height:22px;margin:10px 0 0;font-size:13px;line-height:1.45;font-weight:700}.pnw-assistance-coverage__status.is-neutral{color:#5c6977}.pnw-assistance-coverage__status.is-success{color:#14733b}.pnw-assistance-coverage__status.is-error{color:#b42318}.pnw-assistance-coverage__note{margin:7px 0 0;font-size:11.5px;line-height:1.45;color:#667585}
      body.pnw-assistance-coverage-locked form[data-pnw-coverage-form="true"] [name="add"],body.pnw-assistance-coverage-locked form[data-pnw-coverage-form="true"] [data-pnw-product-submit],body.pnw-assistance-coverage-locked form[data-pnw-coverage-form="true"] button[type="submit"]{opacity:.52;cursor:not-allowed;pointer-events:none}body.pnw-assistance-coverage-locked .shopify-payment-button{display:none!important}
      @media(max-width:640px){.pnw-assistance-coverage__row{grid-template-columns:1fr}.pnw-assistance-coverage__verify{width:100%}}
    `;
    if (!document.getElementById(style.id)) document.head.appendChild(style);

    const panel = document.createElement('div');
    panel.id = 'PnwAssistanceCoverage';
    panel.className = 'pnw-assistance-coverage';
    panel.innerHTML = `
      <div class="pnw-assistance-coverage__head">
        <span class="pnw-assistance-coverage__icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 22s7-6.1 7-13a7 7 0 1 0-14 0c0 6.9 7 13 7 13Z"/><circle cx="12" cy="9" r="2.5"/></svg></span>
        <div><strong>Verifica copertura ritiro</strong><small>Il servizio è disponibile entro 15 km dalla sede Powernet. Inserisci l'indirizzo completo prima di acquistare.</small></div>
      </div>
      <label for="PnwPickupAddress">Indirizzo di ritiro</label>
      <div class="pnw-assistance-coverage__row">
        <input id="PnwPickupAddress" type="text" inputmode="text" autocomplete="street-address" placeholder="Es. Via Roma 10, Capaci (PA)" aria-describedby="PnwPickupStatus PnwPickupNote">
        <button class="pnw-assistance-coverage__verify" type="button" data-pnw-coverage-verify>Verifica copertura</button>
      </div>
      <p id="PnwPickupStatus" class="pnw-assistance-coverage__status is-neutral" data-pnw-coverage-status aria-live="polite">Verifica il tuo indirizzo per attivare il pulsante di acquisto.</p>
      <p id="PnwPickupNote" class="pnw-assistance-coverage__note">Sede Powernet: Via San Erasmo 37, Capaci (PA). La verifica utilizza la distanza stradale indicativa. Dati cartografici © OpenStreetMap contributors.</p>
    `;

    const submitButton = $('[data-pnw-product-submit], [name="add"], button[type="submit"]', mainForm);
    const anchor = submitButton?.closest('.pnw-product-actions, .product-form__buttons, .product-form__submit') || submitButton;
    if (anchor?.parentNode) anchor.parentNode.insertBefore(panel, anchor);
    else mainForm.prepend(panel);

    const addressInput = $('#PnwPickupAddress', panel);
    const verifyButton = $('[data-pnw-coverage-verify]', panel);
    const status = $('[data-pnw-coverage-status]', panel);
    const gatedButtons = () => forms.flatMap((form) => $$('[name="add"], [data-pnw-product-submit], button[type="submit"]', form));

    forms.forEach((form) => {
      form.dataset.pnwCoverageForm = 'true';
      form.dataset.pnwCoverageVerified = 'false';
    });

    gatedButtons().forEach((button) => {
      if (button.dataset.pnwCoverageInitialDisabled === undefined) {
        button.dataset.pnwCoverageInitialDisabled = button.disabled ? 'true' : 'false';
      }
    });

    const setStatus = (message, type = 'neutral') => {
      status.textContent = message;
      status.className = `pnw-assistance-coverage__status is-${type}`;
    };

    const removeCoverageProperties = () => {
      forms.forEach((form) => {
        $$('input[data-pnw-coverage-property]', form).forEach((input) => input.remove());
      });
    };

    const setProperty = (form, key, value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${key}]`;
      input.value = value;
      input.dataset.pnwCoverageProperty = 'true';
      form.appendChild(input);
    };

    const lockPurchase = () => {
      state.verified = false;
      document.body.classList.add('pnw-assistance-coverage-locked');
      forms.forEach((form) => { form.dataset.pnwCoverageVerified = 'false'; });
      gatedButtons().forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      });
    };

    const unlockPurchase = () => {
      state.verified = true;
      document.body.classList.remove('pnw-assistance-coverage-locked');
      forms.forEach((form) => { form.dataset.pnwCoverageVerified = 'true'; });
      gatedButtons().forEach((button) => {
        const wasDisabled = button.dataset.pnwCoverageInitialDisabled === 'true';
        button.disabled = wasDisabled;
        button.setAttribute('aria-disabled', String(wasDisabled));
      });
    };

    const radians = (degrees) => degrees * Math.PI / 180;
    const haversineKm = (lat1, lon1, lat2, lon2) => {
      const earthRadiusKm = 6371;
      const dLat = radians(lat2 - lat1);
      const dLon = radians(lon2 - lon1);
      const a = Math.sin(dLat / 2) ** 2 + Math.cos(radians(lat1)) * Math.cos(radians(lat2)) * Math.sin(dLon / 2) ** 2;
      return 2 * earthRadiusKm * Math.asin(Math.sqrt(a));
    };

    const geocodeAddress = async (rawAddress) => {
      const url = new URL('https://nominatim.openstreetmap.org/search');
      url.searchParams.set('format', 'jsonv2');
      url.searchParams.set('addressdetails', '1');
      url.searchParams.set('limit', '5');
      url.searchParams.set('countrycodes', 'it');
      url.searchParams.set('accept-language', 'it');
      url.searchParams.set('email', 'info@powernetsrl.it');
      url.searchParams.set('q', `${rawAddress}, Sicilia, Italia`);
      const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('geocode_unavailable');
      const results = await response.json();
      if (!Array.isArray(results) || !results.length) throw new Error('address_not_found');
      const candidates = results
        .map((result) => ({ ...result, latNumber: Number(result.lat), lonNumber: Number(result.lon) }))
        .filter((result) => Number.isFinite(result.latNumber) && Number.isFinite(result.lonNumber));
      if (!candidates.length) throw new Error('address_not_found');
      candidates.sort((a, b) => haversineKm(ORIGIN_LAT, ORIGIN_LON, a.latNumber, a.lonNumber) - haversineKm(ORIGIN_LAT, ORIGIN_LON, b.latNumber, b.lonNumber));
      return candidates[0];
    };

    const roadDistanceKm = async (latitude, longitude) => {
      const url = `https://router.project-osrm.org/route/v1/driving/${ORIGIN_LON},${ORIGIN_LAT};${longitude},${latitude}?overview=false&alternatives=false&steps=false`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('route_unavailable');
      const data = await response.json();
      const metres = Number(data?.routes?.[0]?.distance);
      if (data?.code !== 'Ok' || !Number.isFinite(metres)) throw new Error('route_unavailable');
      return metres / 1000;
    };

    const verifyCoverage = async () => {
      const rawAddress = addressInput.value.trim();
      removeCoverageProperties();
      lockPurchase();

      if (rawAddress.length < 8) {
        setStatus('Inserisci via, numero civico e comune per effettuare la verifica.', 'error');
        addressInput.focus();
        return;
      }

      const originalLabel = verifyButton.textContent;
      verifyButton.disabled = true;
      verifyButton.textContent = 'Verifica…';
      setStatus('Sto verificando indirizzo e distanza dalla sede Powernet…', 'neutral');

      try {
        const place = await geocodeAddress(rawAddress);
        const distance = await roadDistanceKm(place.latNumber, place.lonNumber);
        const distanceLabel = `${distance.toFixed(1)} km su strada`;
        const resolvedAddress = place.display_name || rawAddress;

        if (distance <= MAX_DISTANCE_KM) {
          forms.forEach((form) => {
            setProperty(form, 'Indirizzo di ritiro', resolvedAddress);
            setProperty(form, 'Indirizzo inserito', rawAddress);
            setProperty(form, 'Distanza dalla sede', distanceLabel);
            setProperty(form, 'Copertura ritiro', 'Verificata entro 15 km');
          });
          unlockPurchase();
          setStatus(`Copertura disponibile: ${distanceLabel}. Indirizzo riconosciuto: ${resolvedAddress}`, 'success');
        } else {
          setStatus(`Indirizzo fuori copertura: ${distanceLabel}. Il servizio di ritiro è disponibile fino a 15 km dalla sede Powernet.`, 'error');
        }
      } catch (error) {
        if (error?.message === 'address_not_found') {
          setStatus('Indirizzo non trovato. Inserisci via, numero civico, comune e, se possibile, CAP.', 'error');
        } else {
          setStatus('Non è stato possibile verificare la distanza in questo momento. Riprova tra poco.', 'error');
        }
      } finally {
        verifyButton.disabled = false;
        verifyButton.textContent = originalLabel;
      }
    };

    lockPurchase();

    addressInput.addEventListener('input', () => {
      if (state.verified || $$('input[data-pnw-coverage-property]', mainForm).length) {
        removeCoverageProperties();
        lockPurchase();
      }
      setStatus('Indirizzo modificato: ripeti la verifica prima di acquistare.', 'neutral');
    });

    addressInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        verifyCoverage();
      }
    });

    verifyButton.addEventListener('click', verifyCoverage);

    forms.forEach((form) => {
      form.addEventListener('submit', (event) => {
        if (form.dataset.pnwCoverageVerified === 'true') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        setStatus('Prima di acquistare devi verificare un indirizzo di ritiro entro 15 km.', 'error');
        panel.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        window.setTimeout(() => addressInput.focus(), 250);
      }, true);
    });

    document.addEventListener('click', (event) => {
      if (state.verified) return;
      const dynamicCheckout = event.target.closest('.shopify-payment-button, [data-shopify="payment-button"]');
      if (!dynamicCheckout) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      setStatus('Prima di acquistare devi verificare un indirizzo di ritiro entro 15 km.', 'error');
      panel.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    }, true);
  };

  const bootAssistanceCoverage = () => initAssistanceCoverage();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAssistanceCoverage, { once: true });
  } else {
    bootAssistanceCoverage();
  }
  document.addEventListener('shopify:section:load', bootAssistanceCoverage);
})();
